import { AlertCircle, CreditCard, Ticket } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { customerApi, publicApi } from "../../api/client";
import LoadingState from "../../components/common/LoadingState";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import type { EventItem, OrderRegistrationForm } from "../../types/api";
import { formatCurrency, formatEventDate } from "../../utils/format";
import {
  isStorytellingMasterclassEvent,
  loadMasterclassRegistration,
} from "../../utils/masterclassRegistration";
import { launchPaystackPopup } from "../../utils/paystack";

export default function CheckoutPage() {
  const { id = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { customer: account, token } = useCustomerAuth();

  const [event, setEvent] = useState<EventItem | null>(null);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [registrationForm, setRegistrationForm] = useState<OrderRegistrationForm | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tierId = searchParams.get("tier") ?? "";
  const quantity = Math.max(1, Number(searchParams.get("qty") ?? "1"));

  useEffect(() => {
    setIsLoading(true);
    void publicApi
      .getEvent(id)
      .then(({ event: data }) => setEvent(data))
      .catch((requestError) =>
        setError(requestError instanceof Error ? requestError.message : "Could not load checkout")
      )
      .finally(() => setIsLoading(false));
  }, [id]);

  useEffect(() => {
    if (!account) {
      return;
    }

    setCustomer({
      name: account.name,
      email: account.email,
      phone: account.phone,
    });
  }, [account]);

  const selectedTier = useMemo(
    () => event?.ticketTiers.find((tier) => tier.id === tierId) ?? event?.ticketTiers[0] ?? null,
    [event, tierId]
  );

  useEffect(() => {
    if (!event) return;

    if (!isStorytellingMasterclassEvent(event)) {
      setRegistrationForm(null);
      return;
    }

    const savedRegistration = loadMasterclassRegistration(event.id);
    if (!savedRegistration) {
      navigate(`/screening/${event.id}?tier=${tierId}&qty=${quantity}`, { replace: true });
      return;
    }

    setRegistrationForm(savedRegistration);
  }, [event, navigate, quantity, tierId]);

  const subtotal = (selectedTier?.price ?? 0) * quantity;
  const fee = Number((subtotal * 0.035).toFixed(2));
  const total = subtotal + fee;

  if (isLoading) {
    return (
      <div className="aan-ticket-flow min-h-screen bg-black pt-36 text-white">
        <div className="section-frame">
          <LoadingState label="Preparing checkout..." />
        </div>
      </div>
    );
  }

  if (!event || !selectedTier) {
    return (
      <div className="aan-ticket-flow min-h-screen bg-black text-white">
        <Navbar />
        <div className="section-frame pt-36">
          <div className="surface-card px-6 py-12 text-center text-sm font-semibold text-red-500">
            {error ?? "Ticket selection is incomplete"}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="aan-ticket-flow min-h-screen bg-black text-white">
      <Navbar />

      <section className="pb-24 pt-36">
        <div className="section-frame">
            <div className="mb-8 max-w-3xl">
            <div className="font-display text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">AAN Checkout</div>
            <h1 className="mt-2 font-display text-5xl font-black uppercase leading-none text-foreground">Complete payment and generate your QR ticket instantly.</h1>
            <p className="mt-4 text-sm leading-7 text-muted-foreground">
              Your African Art Network ticket stays saved in My Tickets after payment.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
            <form
              className="surface-card p-6 sm:p-8"
              onSubmit={async (submitEvent) => {
                submitEvent.preventDefault();
                setIsSubmitting(true);
                setError(null);

                try {
                  if (!token) {
                    throw new Error("Your session expired. Please sign in again.");
                  }

                  if (isStorytellingMasterclassEvent(event) && !registrationForm) {
                    navigate(`/screening/${event.id}?tier=${tierId}&qty=${quantity}`, { replace: true });
                    return;
                  }

                  const response = await customerApi.checkout(token, {
                    eventId: event.id,
                    customer,
                    items: [{ ticketTierId: selectedTier.id, quantity }],
                    paymentMethod: "paystack",
                    registrationForm,
                  });

                  const popupResponse = await launchPaystackPopup(response.paymentSession);
                  const verified = await customerApi.verifyPayment(token, response.order.reference, {
                    reference: popupResponse.reference || response.paymentSession.reference,
                  });

                  navigate(`/orders/confirmation/${verified.order.reference}`);
                } catch (requestError) {
                  setError(requestError instanceof Error ? requestError.message : "Payment could not be completed");
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              <div className="mb-6 rounded-[24px] border border-line bg-surface-muted px-5 py-4 text-sm text-muted-foreground">
                Signed in as <span className="font-semibold text-foreground">{account?.email}</span>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                {[
                  { label: "Full name", key: "name", type: "text" },
                  { label: "Email address", key: "email", type: "email" },
                  { label: "Phone number", key: "phone", type: "tel" },
                ].map((field) => (
                  <label key={field.key} className={field.key === "phone" ? "md:col-span-2" : ""}>
                    <span className="mb-2 block text-sm font-semibold text-muted-foreground">{field.label}</span>
                    <input
                      type={field.type}
                      required
                      value={customer[field.key as keyof typeof customer]}
                      readOnly={field.key === "email"}
                      onChange={(inputEvent) =>
                        setCustomer((current) => ({
                          ...current,
                          [field.key]: inputEvent.target.value,
                        }))
                      }
                      className={`w-full rounded-[24px] border border-line px-4 py-4 text-sm text-muted-foreground outline-none ${
                        field.key === "email"
                          ? "bg-surface-muted text-muted-foreground"
                          : "bg-surface-muted focus:border-[var(--pp-gold)]"
                      }`}
                    />
                  </label>
                ))}
              </div>

              <div className="mt-8">
                <div className="mb-3 text-sm font-semibold text-muted-foreground">Payment method</div>
                <div className="rounded-[8px] border border-aan-green bg-black px-4 py-5 text-white">
                  <CreditCard size={18} className="text-aan-bright" />
                  <div className="mt-4 font-display text-base font-black uppercase">Paystack</div>
                  <div className="mt-1 text-sm text-slate-300">Card or Mobile Money</div>
                </div>
              </div>

              <p className="mt-6 rounded-[24px] border border-line bg-surface-muted px-5 py-4 text-sm leading-7 text-muted-foreground">
                Choose Card or Mobile Money inside Paystack.
              </p>

              {registrationForm && (
                <div className="mt-6 rounded-[24px] border border-aan-green/60 bg-aan-green/10 px-5 py-4 text-sm leading-7 text-muted-foreground">
                  <div className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-aan-bright">
                    Registration saved
                  </div>
                  <div className="mt-2 text-foreground">{registrationForm.responses.fullName}</div>
                  <div>{registrationForm.responses.email}</div>
                  <div>{registrationForm.responses.phone}</div>
                </div>
              )}

              {error && (
                <div className="mt-6 flex items-start gap-3 rounded-[24px] border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-600">
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="mt-8 rounded-[8px] border border-white/15 bg-black p-5 text-white">
                <div className="flex items-start gap-3">
                  <Ticket size={18} className="mt-1 text-aan-bright" />
                    <div>
                      <div className="font-semibold">Ticket delivery</div>
                      <p className="mt-1 text-sm leading-7 text-slate-300">
                        Your QR ticket is generated only after the backend verifies Paystack and confirms the exact amount.
                      </p>
                    </div>
                  </div>
                </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-6 w-full rounded-full bg-aan-green px-5 py-4 font-display text-base font-black uppercase text-black hover:bg-aan-bright disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting
                  ? "Opening Paystack..."
                  : `Pay ${formatCurrency(total)}`}
              </button>
            </form>

            <aside className="space-y-6">
              <div className="surface-card overflow-hidden">
                <img src={event.image} alt={event.title} className="h-56 w-full object-cover" />
                <div className="p-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">{event.category}</div>
                  <h2 className="mt-2 text-3xl font-bold text-foreground">{event.title}</h2>
                  <p className="mt-3 text-sm text-muted-foreground">{formatEventDate(event.date, event.time)}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {event.venue}, {event.city}
                  </p>
                </div>
              </div>

              <div className="surface-card p-6">
                <div className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">Order summary</div>
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span>
                      {selectedTier.name} x {quantity}
                    </span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Service fee</span>
                    <span>{formatCurrency(fee)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-line pt-4 text-lg font-bold text-foreground">
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
